import { useMemo, useState } from "react";

import { Button } from "@/components/_ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/_ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/_ui/dropdown-menu";
import { Cross2Icon } from "@radix-ui/react-icons";

import { useDatatableContext } from "../use-datatable-context.tsx";

import {
  displayTextForFilterMode,
  FilterByDateMode,
  FilterByDatePayload,
  FilterByDatePayloadSchema,
} from "./functions/filter-by-date.ts";
import { FilterByDateConfigurationView } from "./filter-by-date-configuration-view.tsx";
import { FilterByDateDisplayView } from "./filter-by-date-display-view.tsx";

export interface FilterByDateItem {
  value: string;
  mode: FilterByDateMode;
}

export interface FilterByDateAppliedFilterViewProps {
  column: {
    id: string;
  };
  displayValue: string;
  inputPlaceholder: string;
  allowedFilterModes?: FilterByDateMode[];
}

export const FilterByDateAppliedFilterView = ({
  allowedFilterModes = ["before", "after"],
  inputPlaceholder,
  displayValue,
  ...props
}: FilterByDateAppliedFilterViewProps) => {
  const [showDialog, setShowDialog] = useState(false);

  const columnId = props.column.id;
  const { table, state } = useDatatableContext();
  const { columnFilters } = state;

  const currentColumn = table.getColumn(columnId);

  const filter = useMemo(() => {
    if (!currentColumn) {
      return null;
    }
    if (!currentColumn.getCanFilter()) {
      return null;
    }
    const filterValue = currentColumn.getFilterValue();
    if (!filterValue) {
      return null;
    }
    return filterValue;
  }, [currentColumn]);

  const filterByDateFilter = useMemo(() => {
    if (!filter) {
      return null;
    }

    const safePayload = FilterByDatePayloadSchema.safeParse(filter);
    if (!safePayload.success) {
      return null;
    }

    return safePayload.data;
  }, [filter]);

  const setFilter = (payload: FilterByDatePayload) => {
    currentColumn?.setFilterValue(payload);
  };

  const clearFilter = () => {
    if (!currentColumn) {
      return;
    }
    table.setColumnFilters(columnFilters.slice().filter((filterValue) => filterValue.id !== currentColumn.id));
  };

  const setFilterMode = (filterMode: FilterByDateMode) => {
    if (!filterByDateFilter) {
      return;
    }
    setFilter({
      ...filterByDateFilter,
      mode: filterMode,
    });
  };

  if (!currentColumn) {
    return <></>;
  }

  if (!filter) {
    return <></>;
  }

  if (!filterByDateFilter) {
    return <></>;
  }
  return (
    <div
      className="flex flex-row flex-nowrap items-center gap-0 whitespace-nowrap rounded-md border p-0 text-xs font-light"
      data-testid="filter-by-date-applied-filter-view"
    >
      <div className="flex flex-row items-center gap-1 pl-1 text-xs">
        <FilterByDateDisplayView className="h-3 w-3" displayValue={displayValue} />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild={true}>
          <Button
            variant="ghost"
            size="xs"
            className="h-6 shrink-0 justify-start overflow-x-hidden px-1.5 text-xs font-normal ring-offset-background focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            {displayTextForFilterMode(filterByDateFilter.mode)}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {allowedFilterModes.map((mode) => {
            return (
              <DropdownMenuItem
                key={mode}
                onClick={() => {
                  setFilterMode(mode);
                }}
              >
                {displayTextForFilterMode(mode)}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogTrigger asChild={true}>
          <Button
            variant="ghost"
            size="xs"
            className="h-6 shrink-0 justify-start overflow-x-hidden px-1.5 text-xs font-normal ring-offset-background focus:outline-none focus-visible:outline-none focus-visible:ring-0  focus-visible:ring-offset-0"
          >
            {filterByDateFilter.date}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <FilterByDateConfigurationView
            column={{ id: columnId }}
            inputPlaceholder={inputPlaceholder}
            defaultValue={{
              value: filterByDateFilter.date,
              mode: filterByDateFilter.mode,
            }}
            onFinished={() => {
              setShowDialog(false);
            }}
          />
        </DialogContent>
      </Dialog>
      <Button
        variant="ghost"
        size="xs"
        className="h-6 w-full justify-start overflow-x-hidden px-1.5 text-xs font-normal ring-offset-background focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
        onClick={() => {
          clearFilter();
        }}
      >
        <Cross2Icon className="h-3.5 w-3.5 text-foreground/60" />
      </Button>
    </div>
  );
};
